// The fake api's guided setup (spec/13 §13.1, spec/14 §3.2; F-HQ-03, ADR-0047): an organisation's and a venue's steps,
// skipping a suggested step, HQ's view, and the Go-live gate the venue PATCH asks. Every status is worked out with the
// same `@clavian/domain` rules the server uses, from the fake's own state — the venue's opening hours, whether its PC is
// paired — and from the facts a test sets (`SetupFacts`), so a screen is tested against what the server would say.

import type { HqOrganizationSetup, OrganizationSetup, Venue, VenueSetup, VenueSetupStepId } from '@clavian/api-client';
import {
  type OrganizationSetupStepId,
  SUGGESTED_ORGANIZATION_SETUP_STEPS,
  SUGGESTED_VENUE_SETUP_STEPS,
  type SuggestedVenueSetupStepId,
} from '@clavian/contracts/constants';
import {
  essentialStepsDone,
  missingEssentials,
  organizationSetupSteps,
  organizationSetupSummary,
  setupProgress,
  type SetupSkipFact,
  suggestedStepsDone,
  type VenueEssentialFacts,
  venueSetupSteps,
} from '@clavian/domain/setup';

/** What a test says about a venue's setup beyond the fake's own state. Absent: the defaults below. */
export interface SetupFacts {
  /**
   * How players pay (`price`, ADR-0058): a pass on sale. On by default: the fake's venue sells. A test that gives the
   * fake a pass catalogue (`passes` on the fake server) leaves it out, and the catalogue decides.
   */
  readonly price?: boolean;
  /** A PC paired. On by default, so going live in an older test is not refused. */
  readonly stationPaired?: boolean;
  /** The suggested steps already done. None by default. */
  readonly done?: readonly SuggestedVenueSetupStepId[];
}

/**
 * The organisation's business details as the fake keeps them (ADR-0046; the fake server's `businessDetails` option and
 * its PATCH). Complete when the legal name and the registered address are both there — the record's `complete` flag.
 */
export interface FakeBusinessDetails {
  readonly legalName: string | null;
  readonly pan: string | null;
  readonly registeredAddress: unknown;
}

const COMPLETE_DETAILS: FakeBusinessDetails = { legalName: 'Nitro Gaming Private Limited', pan: null, registeredAddress: {} };

export interface FakeSetupState {
  /** Skipped steps, by venue id. */
  readonly skips: Map<string, Map<VenueSetupStepId, SetupSkipFact>>;
  /** The organisation's own skipped steps (its business details). Absent: none. */
  organizationSkips?: Map<OrganizationSetupStepId, SetupSkipFact>;
}

const SKIPPED_AT = '2026-10-08T09:30:00.000Z';
const MEMBER = 'mem_01k5znw4hmfay8vvr14d2pf2db';

function essentialFacts(venue: Venue, facts: SetupFacts): VenueEssentialFacts {
  const priced = facts.price ?? true;
  const paired = facts.stationPaired ?? true;
  return {
    openingHours: venue.openingHours as unknown as Readonly<Record<string, readonly unknown[]>>,
    price: { passesOnSale: priced ? 1 : 0 },
    stations: { stations: 1, pcs: 1, pairedPcs: paired ? 1 : 0 },
  };
}

export function venueSetupOf(venue: Venue, facts: SetupFacts, state: FakeSetupState): VenueSetup {
  const essential = essentialFacts(venue, facts);
  const done = new Set(facts.done ?? []);
  const suggested = suggestedStepsDone({
    taxRegistrationRecorded: done.has('taxMode'),
    lockdownTestPassed: done.has('lockdownTest'),
    receiptPrinter: done.has('printer'),
    counterUpi: done.has('payments'),
    onlinePayments: false,
    kioskSignUpChosen: done.has('kioskSignUp'),
    offlineCodesIssued: done.has('offlineCodes'),
    offlineSettingsChosen: false,
    staffPinSet: done.has('staffPin'),
    staffBesidesOwners: done.has('staffInvited'),
    sessionRun: done.has('firstSession'),
  });
  const steps = venueSetupSteps({ ...essentialStepsDone(essential), ...suggested }, state.skips.get(venue.id) ?? new Map());
  return {
    venueId: venue.id,
    name: venue.name,
    slug: venue.slug,
    status: venue.status,
    steps: steps.map((step) => ({ id: step.id, kind: step.kind, status: step.status, skip: step.skip })),
    progress: setupProgress(steps),
    missingEssentials: venue.status === 'setup' ? [...missingEssentials(essential)] : [],
  };
}

export function organizationSetupOf(
  organizationId: string,
  venues: readonly Venue[],
  facts: SetupFacts,
  state: FakeSetupState,
  details: FakeBusinessDetails = COMPLETE_DETAILS,
): OrganizationSetup {
  const complete = details.legalName !== null && details.registeredAddress !== null;
  const steps = organizationSetupSteps({ businessDetailsComplete: complete, venues: venues.length }, state.organizationSkips ?? new Map());
  return {
    organizationId,
    steps: steps.map((step) => ({ id: step.id, kind: step.kind, status: step.status, skip: step.skip })),
    businessDetails: { legalName: details.legalName !== null, registeredAddress: details.registeredAddress !== null, pan: details.pan !== null },
    // A venue that has opened had its essentials when it did; the fake keeps them.
    venues: venues.map((venue) => venueSetupOf(venue, venue.status === 'setup' ? facts : { ...facts, stationPaired: true, price: true }, state)),
    anyVenueOpen: venues.some((venue) => venue.status === 'live' || venue.status === 'paused'),
  };
}

/** Where an organisation stands for HQ: its own steps and each venue's essentials. */
export function hqSetupOf(
  organizationId: string,
  venues: readonly Venue[],
  facts: SetupFacts,
  state: FakeSetupState,
  details: FakeBusinessDetails = COMPLETE_DETAILS,
): HqOrganizationSetup {
  const organization = organizationSetupOf(organizationId, venues, facts, state, details);
  const summary = organizationSetupSummary(
    organization.steps,
    organization.venues.map((venue, index) => ({ status: venue.status, createdAt: index, missingEssentials: venue.missingEssentials })),
  );
  return {
    organizationId,
    state: summary.state,
    essentialsDone: summary.essentialsDone,
    essentialsTotal: summary.essentialsTotal,
    ...(summary.nextStep === null ? {} : { nextStep: summary.nextStep }),
    venues: organization.venues.map((venue) => ({
      venueId: venue.venueId,
      name: venue.name,
      status: venue.status,
      essentialsDone: venue.progress.essentialsDone,
      essentialsTotal: venue.progress.essentialsTotal,
      missingEssentials: venue.missingEssentials,
    })),
  };
}

/** Skip or take back a suggested step; `undefined` for an essential one, which the api's path refuses. */
export function applySkip(state: FakeSetupState, venueId: string, step: string, skip: boolean): boolean {
  if (!(SUGGESTED_VENUE_SETUP_STEPS as readonly string[]).includes(step)) return false;
  const venueSkips = state.skips.get(venueId) ?? new Map<VenueSetupStepId, SetupSkipFact>();
  if (skip) venueSkips.set(step as VenueSetupStepId, { at: SKIPPED_AT, by: MEMBER });
  else venueSkips.delete(step as VenueSetupStepId);
  state.skips.set(venueId, venueSkips);
  return true;
}

/** Skip or take back the organisation's suggested step; `false` for its essential one, which the api's path refuses. */
export function applyOrganizationSkip(state: FakeSetupState, step: string, skip: boolean): boolean {
  if (!(SUGGESTED_ORGANIZATION_SETUP_STEPS as readonly string[]).includes(step)) return false;
  const skips = state.organizationSkips ?? new Map<OrganizationSetupStepId, SetupSkipFact>();
  if (skip) skips.set(step as OrganizationSetupStepId, { at: SKIPPED_AT, by: MEMBER });
  else skips.delete(step as OrganizationSetupStepId);
  state.organizationSkips = skips;
  return true;
}

/** The essentials a venue in setup is missing: what the venue PATCH refuses `live` for. */
export function goLiveMissing(venue: Venue, facts: SetupFacts): readonly string[] {
  return venue.status === 'setup' ? missingEssentials(essentialFacts(venue, facts)) : [];
}
